// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyInteraction, TyInteractionChannel } from "@/types";
import { dueState, formatDateTime, pressToOpen } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    interaction: TyInteraction;
    /** The whole card. It opens the contact's own popup, where everything it is too small to say is. */
    onOpen?: () => void;
    /**
     * Closing out a booked call — the one thing a timeline ever asks of the person reading it. It is
     * offered on anything still planned, not only on what is overdue: a call booked for Friday and
     * made on Wednesday is the ordinary case, not an exception.
     */
    onLog?: () => void;
    /**
     * What the contact was **about**, already worded by the screen — which guarantee, or that it
     * belongs to an enquiry. It carries its own drawing, because a crown and a pipeline arrow tell
     * the two apart without being read. Off on a lead's own screen, where the answer is the heading.
     */
    tag?: { label: string; icon?: TyIconName };
};

/** Seven channels, four drawings. A handset says "we spoke" for a call and a WhatsApp alike. */
const CHANNEL_ICONS: Record<TyInteractionChannel, TyIconName> = {
    call: "phone",
    whatsapp: "phone",
    sms: "document",
    email: "document",
    visit: "building",
    catalog: "eye",
    other: "more",
};

/**
 * One line of a timeline — a call, a message, a visit, or one of those **booked for later**.
 *
 * A planned entry is not a separate kind of thing with its own list. It is this card with a date in
 * the future and no outcome yet, so a person reads what they owe in the same place they read what
 * they did. That is the whole argument for having no "next action" field: two lists would have to
 * be reconciled, one list cannot drift.
 *
 * **It is a summary, never the record.** One line of what was said, clipped with an ellipsis, and
 * the rest lives in the popup the card opens. A timeline is scanned down the left edge for the one
 * entry somebody is looking for; a card that grows with whatever was typed into it turns that scan
 * into a scroll, and three long notes push the fourth row off the screen.
 *
 * **Four marks, each in a fixed place**, because somebody scanning a history asks the same four
 * questions of every row and should never hunt for one: the channel as a medallion on the inline
 * edge, the state as a pill at the far end, when under the title, and whose it is at the foot.
 *
 * **What it was about is a chip, not a footnote.** The kind of contact and the guarantee behind it
 * used to be one grey line of small print at the bottom, joined by a dot — which is where a person
 * looks last, for the two facts that decide whether this row is the one they are hunting for.
 */
export default function InteractionCard({ interaction, onOpen, onLog, tag }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const due = dueState(interaction);
    const author = typeof interaction.client === "object" && interaction.client ? interaction.client.name : null;
    const isPlanned = due !== "done";
    const isInbound = interaction.direction === "inbound";

    return (
        <article
            className={`interaction-card interaction-card--${due}`}
            role={onOpen ? "button" : undefined}
            tabIndex={onOpen ? 0 : undefined}
            onClick={onOpen}
            onKeyDown={onOpen ? pressToOpen(onOpen) : undefined}
        >
            <div className="interaction-card__top">
                <span className="interaction-card__badge" aria-hidden="true">
                    <Icon name={CHANNEL_ICONS[interaction.channel]} size={16} />
                </span>

                <div className="interaction-card__identity">
                    <span className="interaction-card__channel">
                        {t.crm.channels[interaction.channel]}
                        {/* Which way it went is not decided for something nobody has done yet. */}
                        {!isPlanned && (
                            <span className={`interaction-card__way interaction-card__way--${isInbound ? "in" : "out"}`}>
                                {t.crm.directions[interaction.direction]}
                            </span>
                        )}
                    </span>
                    <span className="interaction-card__when">
                        <Icon name="calendar" size={12} />
                        {formatDateTime(interaction.date, lang)}
                    </span>
                </div>

                {/* One pill, at the end: either what it is owed as, or how it went. */}
                {due === "overdue" && <span className="interaction-card__state interaction-card__state--overdue">{t.crm.due.overdue}</span>}
                {due === "upcoming" && <span className="interaction-card__state interaction-card__state--upcoming">{t.crm.due.upcoming}</span>}
                {interaction.status && (
                    <span className={`interaction-card__state interaction-card__state--${interaction.status}`}>
                        {t.crm.outcomes[interaction.status]}
                    </span>
                )}
            </div>

            {/* What kind of contact, and what it was about — the two questions a scan is asking. */}
            {(interaction.category || tag) && (
                <div className="interaction-card__marks">
                    {interaction.category && (
                        <span className="interaction-card__mark">
                            <Icon name="filter" size={12} />
                            {t.crm.categories[interaction.category]}
                        </span>
                    )}
                    {tag && (
                        <span className="interaction-card__mark interaction-card__mark--subject">
                            <Icon name={tag.icon ?? "crm"} size={12} />
                            {tag.label}
                        </span>
                    )}
                </div>
            )}

            {/* One line of it, in a box of its own: it is quoted speech, not another field. */}
            <p className="interaction-card__summary">{interaction.summary || interaction.notes || t.crm.noSummary}</p>

            <div className="interaction-card__foot">
                {/* No name means the catalog: nobody on the team was there, which is what inbound means. */}
                <Avatar name={author ?? "?"} size={24} />
                <span className="interaction-card__author">{author ?? t.crm.sources.catalog}</span>
            </div>

            {/*
             * On the card rather than inside the popup because a booked row is work, and work that
             * takes two presses to reach is work that waits until tomorrow. Overdue rows wear it
             * loudest, but an early call is written down the same way.
             */}
            {onLog && isPlanned && (
                <button
                    type="button"
                    className="interaction-card__cta"
                    onClick={(event) => {
                        event.stopPropagation();
                        onLog();
                    }}
                >
                    <Icon name="check" size={14} />
                    {t.crm.logWhatHappened}
                </button>
            )}
        </article>
    );
}
